// GEOMETRY FOR THE BRANCH SOMEBODY JUST OPENED.
//
// THE COMPLAINT. A row in Project Files shows the no-mesh icon whenever the
// viewer has not built that element's geometry - viewer.checkMesh(uid) is
// meshesObject.has(uid), nothing more. On a streaming export that is most rows
// most of the time, because a fragment materialises when it is worth its
// pixels on screen. Open a branch the camera is not pointing at and you get a
// column of icons that read as "this model is missing", when the truth is
// "nobody has fetched this yet". Neighbouring rows of a row you clicked from
// 3D are the worst case: the element you picked has geometry, the five rows
// around it do not, and the tree looks broken.
//
// THE FIX, IN THREE PARTS. The server says which tiles hold a branch
// (GET /model-tree/:projectId/tiles). The viewer is asked to fetch those tiles
// first (streaming.prefetchTiles) and to build those elements whatever their
// size (streaming.ensureUids - its own set, never construction tracking's
// pins). And the row says which of the three states it is in, rather than
// showing one icon for two different facts.
//
// WHY IT IS BOUNDED, measured on the biggest staging commit (344,452
// elements) before any of this was written:
//   - one CATEGORY spans up to 692 tiles. Asking for a category is asking for
//     the model. The budget below is why "open Conduits" cannot do that.
//   - one TYPE group spans 3 tiles at the median, 16 at p25, 569 at worst;
//     16% of the 1,170 type groups are over 32 tiles.
//   - 71,168 elements - a fifth of that commit - carry NO tileKey at all
//     (53,515 of them Electrical Fixtures). No row can fetch those, by any
//     means, and the honest thing is to say so rather than to spin.
//
// SO A BOUND IS HIT OFTEN, AND SAYING SO IS PART OF THE FEATURE. A page that
// quietly asked for 32 of 569 tiles and left the rest showing the no-mesh icon
// would have replaced one wrong impression with another.
import { treeTiles, treeTilesForUids } from "./modelTreeApi";

// Tiles per ask. 32 covers 84% of type groups whole (measured above) and is
// about 30 MB of fragments at BA03's tile sizes - a few seconds, not a reload.
export const TILE_BUDGET = 32;
// Elements per viewer, across every branch opened. A category can hold over
// 100,000; this is the ceiling on the exemption, not on the tree.
export const UID_CAP = 20000;
// Rows per ask. An index-driven section row stands for every element beneath
// it; asking about 40,000 of them is the category ask wearing a hat. This is
// the server's own page size for leaves, so it is "what the tree is showing".
export const ROW_BUDGET = 500;

/**
 * The sentence a reader is owed about what was asked for.
 *
 * Pure and exported so the wording is tested without a viewer, and so the
 * same words appear on the branch and in the row's tooltip.
 */
export function geometryNote(answer) {
  if (!answer) return "";
  const { tileCount = 0, tiles = [], untiled = 0, elementCount = 0, coveredElements = 0, truncated = false } = answer;
  const parts = [];
  if (truncated) {
    parts.push(`Fetching the ${tiles.length} tiles that hold most of this, of ${tileCount} - move the view to bring the rest in.`);
  } else if (tiles.length) {
    parts.push(`Fetching ${tiles.length} tile${tiles.length === 1 ? "" : "s"} for ${coveredElements} element${coveredElements === 1 ? "" : "s"}.`);
  }
  if (untiled > 0) {
    // NOT a failure of this ask, and not something a retry would fix: the
    // export never recorded where these elements' geometry lives.
    parts.push(untiled === elementCount
      ? "This export does not record where these elements' geometry is, so the rows cannot fetch it."
      : `${untiled} of ${elementCount} were exported without a tile, so their rows cannot fetch geometry.`);
  }
  return parts.join(" ");
}

/**
 * What one row's icon should say. Three facts, not two.
 *
 *   built   - the viewer has the geometry; no icon.
 *   asked   - we have asked for its tile and it is on its way.
 *   untiled - the export never placed it; asking again will not help.
 *   absent  - not asked for, not built. The original meaning of the icon.
 */
export function meshState(uid, { hasMesh, asked, untiled } = {}) {
  if (!uid) return "built";
  if (hasMesh) return "built";
  if (untiled) return "untiled";
  if (asked) return "asked";
  return "absent";
}

export const MESH_STATE_TITLE = {
  built: "",
  asked: "Geometry is on its way - this element's tile is being fetched",
  untiled: "This export did not record where this element's geometry is, so it cannot be fetched from here",
  absent: "Geometry for this element has not been loaded yet - it loads when the view reaches it",
};

/**
 * WHICH OPEN BRANCHES STILL NEED ASKING FOR.
 *
 * Pure, so the walk is tested without a viewer or a server. `openKeys` are the
 * library's own open-node keys (ancestors joined with "/"), `asked` is the set
 * of node keys already asked for. A node is a candidate when it carries a
 * scope (server-built nodes do; a legacy per-document tree does not, and
 * legacy projects have every mesh loaded anyway).
 *
 * THE DEEPEST OPEN NODE WINS. Opening Conduits opens its families and types in
 * turn; asking at every level would ask for the category - 692 tiles on the
 * biggest staging commit - to get at one type. So an ancestor is dropped when
 * a descendant of it is also open.
 */
export function branchesToAsk(sections, openKeys, asked, hasUids) {
  const open = openKeys instanceof Set ? openKeys : new Set(openKeys || []);
  const done = asked instanceof Set ? asked : new Set(asked || []);
  if (!open.size) return [];
  const found = [];
  // A BRANCH IS WORTH ASKING ABOUT IF IT HAS ROWS, with or without a scope.
  //
  // This first required node.scope, which only the server-built branch
  // (helpers/modelTreeApi.serverBranch, built on a 3D click) carries - so on
  // the tree people actually look at, the index-driven one, the whole feature
  // was inert. Driving the page is what showed it: zero /model-tree calls and
  // ensured 0 after opening four branches. The ask is by uniqueId anyway, so a
  // scope was never needed; it is kept only as the fallback for a branch whose
  // rows are not listed yet.
  const carries = typeof hasUids === "function" ? hasUids : (n) => !!(n && ((n.allUids && n.allUids.length) || n.meshInfo));
  const walk = (node, path) => {
    if (!node) return;
    const key = path ? `${path}/${node.key || node.id || ""}` : (node.key || node.id || "");
    const kids = Array.isArray(node.nodes) ? node.nodes : [];
    const worth = node.scope || carries(node) || kids.some(carries);
    if (worth && open.has(key) && !done.has(key)) found.push({ key, scope: node.scope || null, node });
    for (const kid of kids) walk(kid, key);
  };
  for (const section of (sections || [])) {
    for (const root of (Array.isArray(section) ? section : [section])) walk(root, "");
  }
  // Drop an ancestor when something below it is open too: the narrower scope
  // is the one worth fetching.
  return found.filter((f) => !found.some((o) => o !== f && o.key.startsWith(`${f.key}/`)));
}

/**
 * Ask for the geometry of one branch.
 *
 * `scope` is {category, family, type} - the same shape /model-tree/children
 * takes. `uids` are the leaf uniqueIds the tree is showing for that branch
 * (at most a page of them); they are what the exemption is granted to, so a
 * branch whose rows are not listed yet asks for its tiles and nothing more.
 *
 * NEVER THROWS. A tree that cannot fetch geometry must still open: this is an
 * improvement on the row's icon, not a precondition for the row.
 */
export async function askForBranchGeometry({
  projectId, commit, scope, uids = [], viewer, tileBudget = TILE_BUDGET, uidCap = UID_CAP,
} = {}) {
  const none = { asked: false, tiles: [], note: "", untiled: 0, truncated: false, uids: 0 };
  const rowsIn = (uids || []).filter(Boolean);
  // Either a scope to ask about, or rows to ask about. With neither there is no
  // question to put.
  const haveScope = !!(scope && scope.category !== undefined && scope.category !== null);
  if (!projectId || (!haveScope && !rowsIn.length)) return none;
  const streaming = viewer && viewer.streaming;
  // A legacy project loads every mesh up front: there is nothing to fetch and
  // no icon to explain. Saying nothing is correct here.
  if (!streaming || typeof streaming.prefetchTiles !== "function") return none;
  // WHICH QUESTION TO ASK. When the tree is showing rows, ask about THOSE
  // rows: asking by scope answers about the fullest tiles of the whole branch,
  // and on BA03's Conduits (18,686 elements, 549 tiles) the 32 fullest hold
  // 3,760 elements that need not include a single row on screen. Only a branch
  // whose rows are not listed yet is asked for by scope.
  // THE ROWS A READER CAN ACTUALLY SEE. An index-driven row stands for every
  // element under it - tens of thousands on BA03 - and asking about all of
  // them would be the category ask again by another route. The first
  // ROW_BUDGET are the ones on screen; the rest arrive as the view reaches
  // them, exactly as they do today.
  const rows = rowsIn.slice(0, ROW_BUDGET);
  let answer;
  try {
    answer = rows.length
      ? await treeTilesForUids(projectId, rows, { limit: tileBudget, commit })
      : await treeTiles(projectId, { ...scope, limit: tileBudget, commit });
  } catch (e) {
    return { ...none, error: (e && e.message) || "could not ask which tiles hold this" };
  }
  const tiles = ((answer && answer.tiles) || []).map((t) => t.tileKey).filter(Boolean);
  if (tiles.length) {
    try { streaming.prefetchTiles(tiles); } catch (e) { /* a hint that cannot be given is not a failure */ }
  }
  let ensured = { uids: 0, truncated: false };
  // The exemption goes to the rows that can actually get geometry. An element
  // this export never placed is left out of it: granting it the exemption
  // would be a promise nothing can keep.
  const untiledSet = new Set(((answer && answer.untiledUids) || []).map(String));
  const list = rows.filter((u) => !untiledSet.has(String(u)));
  if (list.length && typeof streaming.ensureUids === "function") {
    try { ensured = streaming.ensureUids(list, { cap: uidCap }) || ensured; } catch (e) { /* as above */ }
  }
  return {
    asked: tiles.length > 0 || list.length > 0,
    // Named, so the rows that cannot be fetched say so instead of spinning.
    untiledUids: ((answer && answer.untiledUids) || []).map(String),
    notInCommit: (answer && answer.notInCommit) || 0,
    tiles,
    tileCount: (answer && answer.tileCount) || 0,
    untiled: (answer && answer.untiled) || 0,
    elementCount: (answer && answer.elementCount) || 0,
    coveredElements: (answer && answer.coveredElements) || 0,
    truncated: !!(answer && answer.truncated),
    uids: ensured.uids || 0,
    uidsTruncated: !!ensured.truncated,
    note: geometryNote(answer),
  };
}
